import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";

import { SalesOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import type { SalesOrderPeekContext } from "@/components/hub/SalesOrderPeekHoverHintBody";
import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";
import type { SearchableSelectOption } from "@/components/ui/SearchableSelect";

import {
  fetchQualitySalesOrderQuickSearch,
  type QualitySalesOrderSearchRow,
} from "./casesApi";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
}

function salesOrderNumberLabel(row: Pick<QualitySalesOrderSearchRow, "id" | "number">): string {
  const number = row.number?.trim();
  return number != null && number !== "" ? number : `Sales order ${row.id}`;
}

function salesOrderDescription(row: QualitySalesOrderSearchRow): string {
  return [row.client_name?.trim(), row.customer_purchase_order_number?.trim(), row.status?.trim()]
    .filter((part): part is string => part != null && part !== "")
    .join(" · ");
}

function salesOrderPeek(row: QualitySalesOrderSearchRow): SalesOrderPeekContext {
  return {
    sales_order_number: row.number,
    client_name: row.client_name,
    customer_purchase_order_number: row.customer_purchase_order_number,
    sgs_ship_by_date: row.sgs_ship_by_date,
  };
}

function toSearchOption(row: QualitySalesOrderSearchRow): SearchableSelectOption {
  return {
    value: String(row.id),
    label: salesOrderNumberLabel(row),
    description: salesOrderDescription(row) || null,
  };
}

export function QualitySalesOrderPicker({
  id,
  values,
  knownLabels,
  knownPeeks,
  disabled,
  onChange,
}: {
  id: string;
  values: number[];
  knownLabels?: Record<number, string | null | undefined>;
  knownPeeks?: Record<number, SalesOrderPeekContext | null | undefined>;
  disabled?: boolean;
  onChange: (salesOrderIds: number[]) => void;
}): JSX.Element {
  const [searchValue, setSearchValue] = useState("");
  const [knownRows, setKnownRows] = useState<Map<number, QualitySalesOrderSearchRow>>(new Map());
  const debouncedSearch = useDebouncedValue(searchValue.trim(), 250);
  const lookupQuery = values[0] != null ? String(values[0]) : "";
  const searchQuery = debouncedSearch.length >= 2 ? debouncedSearch : lookupQuery;

  const searchResultsQuery = useQuery({
    queryKey: ["quality", "sales-order-quick-search", searchQuery],
    queryFn: () => fetchQualitySalesOrderQuickSearch(searchQuery),
    enabled: searchQuery.length >= 2 || (values.length > 0 && searchQuery === lookupQuery),
    staleTime: 20_000,
  });

  useEffect(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    if (rows.length === 0) {
      return;
    }
    setKnownRows((current) => {
      const next = new Map(current);
      for (const row of rows) {
        next.set(row.id, row);
      }
      return next;
    });
  }, [searchResultsQuery.data?.rows]);

  const selectedRows = values.map((id) => ({
    id,
    row: knownRows.get(id) ?? null,
    chipLabel:
      knownRows.get(id) != null
        ? salesOrderNumberLabel(knownRows.get(id)!)
        : knownLabels?.[id]?.trim() || `Sales order ${id}`,
    chipPeek:
      knownRows.get(id) != null
        ? salesOrderPeek(knownRows.get(id)!)
        : knownPeeks?.[id] ?? (knownLabels?.[id] ? { sales_order_number: knownLabels[id] } : undefined),
  }));

  const options = useMemo(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    const byId = new Map<number, QualitySalesOrderSearchRow>();
    for (const row of rows) {
      byId.set(row.id, row);
    }
    for (const id of values) {
      const selected = knownRows.get(id);
      if (selected != null) {
        byId.set(selected.id, selected);
      }
    }
    const fromRows = [...byId.values()].map(toSearchOption);
    const present = new Set(fromRows.map((option) => option.value));
    for (const selected of selectedRows) {
      if (!present.has(String(selected.id))) {
        fromRows.push({
          value: String(selected.id),
          label: selected.chipLabel,
        });
      }
    }
    return fromRows;
  }, [searchResultsQuery.data?.rows, knownRows, values, selectedRows]);

  return (
    <SearchableMultiSelect
      id={id}
      variant="chips"
      alwaysShowChips
      selectedValues={new Set(values.map(String))}
      onSelectedValuesChange={(next) => {
        const ids = [...next]
          .map((value) => Number(value))
          .filter((parsed) => Number.isFinite(parsed) && parsed >= 1);
        onChange(ids);
        setSearchValue("");
      }}
      options={options}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      placeholder="Search GSO number, client, or customer PO"
      searchPlaceholder="Search GSO number, client, or customer PO"
      emptyMessage={
        searchValue.trim().length < 2
          ? "Type at least two characters to search sales orders."
          : "No sales orders match."
      }
      isLoading={searchResultsQuery.isFetching}
      disabled={disabled}
      panelMinWidth={320}
      renderChip={({ option, disabled: chipDisabled, onRemove }) => {
        const salesOrderId = Number(option.value);
        const selected = selectedRows.find((row) => row.id === salesOrderId);
        return (
          <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-800">
            <SalesOrderDetailLink
              salesOrderId={salesOrderId}
              salesOrderNumber={selected?.chipLabel ?? option.label}
              peek={selected?.chipPeek}
              className="text-xs"
            />
            <button
              type="button"
              className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none"
              aria-label={`Remove ${selected?.chipLabel ?? option.label}`}
              disabled={chipDisabled}
              onClick={(event) => {
                event.stopPropagation();
                onRemove();
              }}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        );
      }}
    />
  );
}
